/* eslint-disable no-console */

/**
 * Solves the problem of users having cached versions of the SPA that are out of sync 
 * with the deployed backend. Periodically checks if a new version has been deployed 
 * and notifies users with an option to refresh.
 * 
 * ## How It Works
 * 
 * 1. **Version Detection**: Periodically fetches `/app/version.json` manifest file,
 *    which is generated at build time and contains a hash representing the current
 *    build and compares the build hash to a stored hash in sessionStorage. The manifest
 *    contains webpack hashes extracted from built files, ensuring any code change
 *    results in a new version hash.
 * 
 * 2. **Activity-Based Polling**: Uses an ActivityBasedTimer that only checks for 
 *    updates when users are actively using the app (navigating routes), preserving 
 *    mobile battery life instead of continuous background polling.
 * 
 * 3. **User Notification**: When a new version is detected, shows a non-intrusive 
 *    notification allowing users to refresh when ready.
 * 
 * ## Configuration
 * - **Normal check**: Every 10 minutes (when user is active)
 * - **Forced check**: Once every 24 hours (fallback)
 * 
 * ## Browser Storage
 * - `rc-app-version-hash`: Current build hash in sessionStorage
 * - `rc-notification-showing`: Alert visibility state
 * 
 * ## Build Process
 * 1. `npm run build` creates dist with webpack-hashed files
 * 2. `npm run create-version-manifest` extracts hashes and creates public/version.json
 * 3. Deployment moves files to target directory
 * 
 * ## Testing
 * - Dev mode: Use `npm run serve:prod` then `npm run build` after changes
 * - Production: Deploy new version, keep tab open, navigate routes, wait up to 10min
 */

import ActivityBasedTimer from '@/js/ActivityBasedTimer.js';

export const STORAGE_KEY_VERSION_HASH = 'rc-app-version-hash';
export const STORAGE_KEY_ALERT_VISIBILITY = 'rc-notification-showing';

let _is_initialized = false; // ensure init is only called once per full page reload by storing in module scope
let _current_timer_id = null;
let _new_version_available = false;
let _versioning_timer = ActivityBasedTimer();

export const getIsInitialized = () => _is_initialized;
export const setIsInitialized = (value) => { _is_initialized = value; };
export const getCurrentTimerId = () => _current_timer_id;
export const setCurrentTimerId = (value) => { _current_timer_id = value; };
export const getIsNewVersionAvailable = () => _new_version_available;
export const setIsNewVersionAvailable = (value) => { _new_version_available = value; };
export const getVersioningTimer = () => _versioning_timer;

/**
 * Check for new version by fetching the version manifest, storing the version hash in sessionStorage
 * Will bail early if alert is already showing
 * @returns {Promise<boolean>} True if a new version is available, false otherwise
 */
export async function checkForNewVersion() {
  if (sessionStorage.getItem(STORAGE_KEY_ALERT_VISIBILITY) === 'true') {
    return true; // no need to poll since already showing alert
  }

  const versionInfo = await _fetchVersionJson();
  if (!versionInfo) return false;

  const newVersionHash = versionInfo.buildHash;
  const currentVersion = sessionStorage.getItem(STORAGE_KEY_VERSION_HASH);

  if (currentVersion === null) {
    storeVersionHash(newVersionHash);
    _new_version_available = false;
    return false; // no need to check further since there's no stored version data to compare against
  }

  const versionsAreDifferent = currentVersion !== newVersionHash;
  
  if (versionsAreDifferent) {
    storeVersionHash(newVersionHash);
    _new_version_available = true;
  } else {
    _new_version_available = false;
  }

  return versionsAreDifferent;
}

/**
 * Refresh the application to get the new version
 * Clears the stored version hash from session storage
 */
export function refreshToNewVersion() {
  sessionStorage.removeItem(STORAGE_KEY_VERSION_HASH);
  sessionStorage.removeItem(STORAGE_KEY_ALERT_VISIBILITY);
  window.location.reload(true); // FireFox only param - https://developer.mozilla.org/en-US/docs/Web/API/Location/reload#forceget
}

/**
 * Store the version hash in sessionStorage
 * @param {string} hash
 */
export function storeVersionHash(hash) {
  if (typeof hash === 'string' && hash.length > 0) {
    sessionStorage.setItem(STORAGE_KEY_VERSION_HASH, hash);
  }
}

// Private Methods

/**
 * Fetch version.json with cache-busting headers
 * @returns {Promise<Object|null>} Version info object or null if failed
 */
async function _fetchVersionJson() {
  try {
    const response = await fetch(`/app/version.json?t=${Date.now()}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to fetch version.json:', error);
    return null;
  }
}

export default {
  STORAGE_KEY_VERSION_HASH,
  STORAGE_KEY_ALERT_VISIBILITY,

  getIsInitialized,
  setIsInitialized,
  getCurrentTimerId,
  setCurrentTimerId,
  getIsNewVersionAvailable,
  setIsNewVersionAvailable,
  getVersioningTimer,

  checkForNewVersion,
  refreshToNewVersion,
  storeVersionHash,
};
